// packages/web-ui/src/components/SessionList/DeleteConfirmDialog.tsx
//
// W2-S8 — Hard-delete confirmation dialog.
//
// RB-locked answer #5 (UX §14 line 2158): the JSON file at
// `<workspaceRoot>/.tessa-lean/sessions/<sessionId>.json` is unlinked from
// disk. There is NO soft-archive. Wording matters — "Permanently delete"
// + "This cannot be undone." are part of the user-awareness contract.
//
// Implementation note: this is a minimal ARIA-correct alertdialog. We
// avoid pulling in `@base-ui/react`'s `<AlertDialog>` primitive because
// (a) the dep is not yet wired in this story's lockfile pin and (b) the
// surface here is small enough that the extra primitive cost is not
// justified. Focus management uses a `useEffect` to focus the Cancel
// button on mount (auto-focus on Cancel prevents accidental confirm).

import type { JSX } from 'react';
import { useEffect, useRef } from 'react';

export interface DeleteConfirmDialogProps {
  open: boolean;
  sessionTitle: string;
  isDeleting?: boolean;
  onCancel: () => void;
  onConfirm: () => void;
}

export function DeleteConfirmDialog({
  open,
  sessionTitle,
  isDeleting = false,
  onCancel,
  onConfirm,
}: DeleteConfirmDialogProps): JSX.Element | null {
  const cancelRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (open) {
      cancelRef.current?.focus();
    }
  }, [open]);

  // Esc closes the dialog (cancel)
  useEffect(() => {
    if (!open) return;
    function onKey(e: KeyboardEvent): void {
      if (e.key === 'Escape') {
        e.preventDefault();
        onCancel();
      }
    }
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('keydown', onKey);
    };
  }, [open, onCancel]);

  if (!open) return null;

  const heading = `Permanently delete "${sessionTitle}"?`;

  return (
    <div
      data-testid="delete-confirm-dialog"
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60"
      onClick={(e) => {
        if (e.target === e.currentTarget) onCancel();
      }}
    >
      <div
        role="alertdialog"
        aria-modal="true"
        aria-labelledby="delete-confirm-heading"
        aria-describedby="delete-confirm-body"
        className="bg-cream-50 border-taupe-500 mx-4 w-full max-w-md rounded-none border p-5"
      >
        <h2
          id="delete-confirm-heading"
          data-testid="delete-confirm-dialog-heading"
          className="text-fg text-base font-extrabold tracking-[-0.01em]"
        >
          {heading}
        </h2>
        <p
          id="delete-confirm-body"
          data-testid="delete-confirm-dialog-body"
          className="text-taupe-700 mt-2 text-sm"
        >
          This cannot be undone. The session file will be removed from disk.
        </p>
        <div className="mt-4 flex justify-end gap-2">
          <button
            ref={cancelRef}
            type="button"
            data-testid="delete-confirm-dialog-cancel"
            className="border-taupe-500 hover:border-taupe-900 hover:bg-cream-100 text-fg rounded-none border px-3 py-1.5 text-[11px] font-semibold uppercase tracking-wide"
            onClick={onCancel}
            disabled={isDeleting}
          >
            Cancel
          </button>
          <button
            type="button"
            data-testid="delete-confirm-dialog-confirm"
            className="bg-audi-red hover:bg-audi-red-hover rounded-none px-3 py-1.5 text-[11px] font-semibold uppercase tracking-wide text-white disabled:opacity-60"
            onClick={onConfirm}
            disabled={isDeleting}
            aria-busy={isDeleting ? 'true' : undefined}
          >
            {isDeleting ? 'Deleting…' : 'Delete'}
          </button>
        </div>
      </div>
    </div>
  );
}
